import { FiAlertTriangle, FiArrowRight, FiCreditCard, FiHash, FiX } from 'react-icons/fi';

const DEFAULT_MESSAGE = 'This investor already has an active onboarding under the selected partner.';

function DetailChip({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex flex-col items-center gap-1.5 rounded-xl bg-white/90 px-4 py-3.5 shadow-sm ring-1 ring-amber-200/60">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-amber-700">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-800/80">
        {label}
      </span>
      <span className="max-w-full break-all text-center text-sm font-semibold text-gray-900">
        {value}
      </span>
    </div>
  );
}

/**
 * Shown when start-mobile or check-pan returns a 409 duplicate-folio conflict.
 * Admin-portal version — no Lottie, green primary button.
 *
 * @param {{
 *   open: boolean;
 *   message?: string;
 *   folioNumber?: string | null;
 *   pan?: string;
 *   onClose: () => void;
 *   onGoToInvestors: () => void;
 * }} props
 */
export default function AlreadyOnboardedModal({
  open,
  message = '',
  folioNumber = null,
  pan = '',
  onClose,
  onGoToInvestors,
}) {
  if (!open) return null;

  const displayMessage = String(message || '').trim() || DEFAULT_MESSAGE;
  const folio = folioNumber ? String(folioNumber).trim() : '';
  const panLabel = pan ? String(pan).trim().toUpperCase() : '';
  const hasDetails = Boolean(panLabel || folio);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4 backdrop-blur-[6px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="already-onboarded-title"
    >
      <div className="relative w-full max-w-[420px] overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
          aria-label="Close"
        >
          <FiX className="h-5 w-5" aria-hidden />
        </button>

        {/* Header band */}
        <div className="relative bg-gradient-to-b from-amber-50 via-amber-50/80 to-white px-6 pb-2 pt-8 text-center">
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[radial-gradient(ellipse_at_top,rgba(251,191,36,0.25),transparent_70%)]"
            aria-hidden
          />
          <div className="relative mx-auto flex h-[96px] w-[96px] items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-amber-200/30 blur-md" aria-hidden />
            <span className="relative flex h-full w-full items-center justify-center rounded-full bg-white/80 shadow-inner ring-4 ring-amber-100/90">
              <FiAlertTriangle className="h-10 w-10 text-amber-500" aria-hidden />
            </span>
          </div>

          <span className="relative mt-4 inline-flex items-center rounded-full border border-amber-200/80 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-800 shadow-sm">
            Existing investor
          </span>

          <h2
            id="already-onboarded-title"
            className="relative mt-3 text-[1.25rem] font-semibold leading-snug tracking-tight text-gray-900"
          >
            Already onboarded
          </h2>
        </div>

        <div className="px-6 pb-7 pt-4 text-center sm:px-8">
          <p className="text-sm leading-relaxed text-gray-600">{displayMessage}</p>

          {hasDetails ? (
            <div className="mt-5 rounded-2xl bg-gradient-to-br from-amber-50/90 to-orange-50/50 p-4 ring-1 ring-amber-100">
              <p className="mb-3 text-xs font-medium text-amber-900/70">
                Registered under this partner
              </p>
              <div className={`grid gap-3 ${panLabel && folio ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <DetailChip icon={FiCreditCard} label="PAN" value={panLabel} />
                <DetailChip icon={FiHash} label="Folio" value={folio} />
              </div>
            </div>
          ) : null}

          <button
            type="button"
            onClick={onGoToInvestors}
            className="group mt-6 flex w-full items-center justify-center gap-2.5 rounded-xl bg-green-700 px-6 py-3.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2"
          >
            <span>Go to investors list</span>
            <FiArrowRight
              className="text-base transition-transform duration-200 ease-out group-hover:translate-x-0.5"
              aria-hidden
            />
          </button>
        </div>
      </div>
    </div>
  );
}
